/**
 * BroadcastChannel parity for the legacy `clientRefresh_channel`
 * (`broadcast-clientrefresh.service.ts`).
 *
 * Contract (.docs/legacy-globals-matrix.md §3):
 *   Payload: `{ senderId: string /* tabId *\/, data: any }`
 *   Listener: ignores its own senderId; on remote message calls
 *   `window.EY_RAPToR_PortalRefresh()`.
 *
 * Cross-shell parity is required during the canary period — an old Angular
 * tab posting to `clientRefresh_channel` must trigger a refresh in a new
 * React tab and vice versa.
 */

const CHANNEL_NAME = 'clientRefresh_channel';

/** Per-tab unique sender id; stable for the page lifetime. */
const SENDER_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export interface ClientRefreshPayload {
  senderId: string;
  data: {
    clientId: string;
    previousClientId: string | null;
    /** Origin of the switch — for telemetry only. */
    source: 'user-switch' | 'url-change' | 'reset';
  };
}

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel {
  if (channel) return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

export function getSenderId(): string {
  return SENDER_ID;
}

/** Post a client-refresh message to all other tabs/shells. */
export function postClientRefresh(data: ClientRefreshPayload['data']): void {
  try {
    getChannel().postMessage({ senderId: SENDER_ID, data } satisfies ClientRefreshPayload);
  } catch {
    // BroadcastChannel can fail in some embedded contexts; refresh is
    // best-effort — swallow to keep the local switch flow alive.
  }
}

/**
 * Subscribe to remote client-refresh messages. Returns an unsubscribe fn.
 * Messages from the same tab (matching senderId) are filtered out — same
 * behaviour as the legacy service.
 */
export function subscribeClientRefresh(
  handler: (payload: ClientRefreshPayload) => void,
): () => void {
  const ch = getChannel();
  const listener = (event: MessageEvent<ClientRefreshPayload>) => {
    const msg = event.data;
    if (!msg || msg.senderId === SENDER_ID) return;
    handler(msg);
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

// ── Favorite toggle cross-tab broadcast ─────────────────────────────────────
// When a user stars/unstars a client in one tab the other tabs must reflect the
// change immediately (without waiting for staleTime to expire or a manual
// refresh). Uses a dedicated channel so it doesn't interfere with the
// client-switch flow above.

const FAVORITE_CHANNEL_NAME = 'clientFavorite_channel';

export interface FavoriteTogglePayload {
  senderId: string;
  data: { clientId: string; favorite: boolean };
}

let favoriteChannel: BroadcastChannel | null = null;

function getFavoriteChannel(): BroadcastChannel {
  if (favoriteChannel) return favoriteChannel;
  favoriteChannel = new BroadcastChannel(FAVORITE_CHANNEL_NAME);
  return favoriteChannel;
}

/** Notify other tabs that a client's favorite flag changed. */
export function postFavoriteToggle(data: FavoriteTogglePayload['data']): void {
  try {
    getFavoriteChannel().postMessage({
      senderId: SENDER_ID,
      data,
    } satisfies FavoriteTogglePayload);
  } catch {
    // Best-effort — swallow so the local toggle is not affected.
  }
}

/** Listen for remote favorite-toggle messages. Returns an unsubscribe fn. */
export function subscribeFavoriteToggle(
  handler: (payload: FavoriteTogglePayload) => void,
): () => void {
  const ch = getFavoriteChannel();
  const listener = (event: MessageEvent<FavoriteTogglePayload>) => {
    const msg = event.data;
    if (!msg || msg.senderId === SENDER_ID) return;
    handler(msg);
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}
